<template>
  <main
    class="expression-editor-modal"
  >
    <header class="modal-panel__header">
      <h1 class="modal__title">
        {{ $t("advancedsearch.expressions.title") }}
      </h1>
      <p>{{ $t("advancedsearch.expressions.subtitle") }}</p>
    </header>
    <div
      class="expression-editor__grid"
      @keydown="handleInput"
    >
      <div>
        <div class="expression-editor__infos">
          <p
            id="expressionDisplayLabel"
            class="small expression-editor__label"
          >
            {{ $t("advancedsearch.expressions.expression") }}
          </p>
        </div>
        <div
          ref="expressionWrapper"
          tabindex="0"
          class="expression-editor__display-wrapper"
          :class="{ 'expression-editor__display-wrapper--danger': showStatusBorder }"
        >
          <!-- FIXME: this transition does not work properly after clicking a button with fillSequence -->
          <div
            class="expression-editor__display"
            aria-labelledby="expressionDisplayLabel"
          >
            <Transition
              name="fade"
              mode="out-in"
            >
              <div
                v-if="modelValue.sequence.length === 0"
                key="expression-placeholder"
                class="expression-editor__placeholder"
              >
                <p>{{ $t('advancedsearch.expressions.placeholder.prefill') }}</p>
                <div class="expression-editor__placeholder-controls">
                  <button
                    v-for="op in ['and', 'or']"
                    :key="`prefill-${op}`"
                    class="expression-editor__placeholder-button"
                    @click.prevent="fillSequence(op)"
                  >
                    <p class="empty" />
                    <p>{{ $t(`logical.${op}.label`) }}</p>
                    <p class="empty" />
                    <p>{{ $t(`logical.${op}.label`) }}</p>
                    <p class="empty" />
                    <p class="description">
                      {{ $t(`advancedsearch.expressions.criteria.${op}`) }}
                    </p>
                  </button>
                </div>
                <p class="expression-editor__placeholder-separator">
                  {{ $t("advancedsearch.expressions.placeholder.separator") }}
                </p>
                <p>
                  {{ $t("advancedsearch.expressions.placeholder.manual") }}
                </p>
              </div>
              <div
                v-else
                key="expression-content"
              >
                <ol>
                  <li>
                    <div
                      ref="track-0"
                      class="track"
                      aria-hidden="true"
                      :class="{ current: currentIndex === 0 }"
                      @click.prevent="setCursor(0, false)"
                    />
                  </li>

                  <li
                    v-for="(element, idx) in modelValue.sequence"
                    :key="`${element.value}-${idx}`"
                    :style="{ paddingLeft: 1 * element.indentationLevel + 'rem' }"
                    class="item"
                    @click.prevent="setCursor(idx+1, false)"
                  >
                    <FieldInfo
                      v-if="element.group === 'FieldNode'"
                      :closeable="true"
                      :field="modelValue.fields[element.value]"
                      @on-close="removeAtIndex(idx)"
                    />
                    <OperationNode
                      v-else-if="element.group === 'OperationNode'"
                      :model-value="element.value"
                      @on-close="removeAtIndex(idx)"
                    />
                    <ParenthesisNode
                      v-else-if="element.group === 'ParenthesisNode'"
                      :model-value="element.value"
                      @on-close="removeAtIndex(idx)"
                    />
                  </li>
                  <div
                    :key="`track-${idx}`"
                    :ref="`track-${idx+1}`"
                    class="track"
                    aria-hidden="true"
                    :class="{ current: currentIndex === idx + 1 }"
                    @click.prevent="currentIndex = idx + 1"
                  />
                </ol>
              </div>
            </Transition>
          </div>
          <!-- FIXME: style this button properly -->
          <Transition
            name="fade"
            mode="out-in"
          >
            <button
              v-if="modelValue.sequence.length !== 0"
              class="control-btn"
              style="bottom: 1rem; right: 1.5rem;"
              @click.prevent="clearSequence"
            >
              <svg
                width="19"
                height="25"
                xmlns="http://www.w3.org/2000/svg"
              ><g
                stroke="var(--gray)"
                stroke-width=".84"
                fill="none"
                fill-rule="evenodd"
                stroke-linecap="round"
                stroke-linejoin="round"
              ><path d="M6.667 3.09h5.666V1.84a.84.84 0 00-.84-.84H7.507a.84.84 0 00-.84.84v1.25h0zM2.214 6.436h14.572V23.16a.84.84 0 01-.84.84H3.054a.84.84 0 01-.84-.84V6.436h0zM1 6.436h17V3.93a.84.84 0 00-.84-.84H1.84a.84.84 0 00-.84.84v2.506h0zM9.5 10.618v9.2M5.452 10.618v9.2M13.548 10.618v9.2" /></g></svg>
            </button>
            <div v-else />
          </Transition>
          <Transition
            name="fade"
            mode="out-in"
          >
            <div
              v-if="showStatusBorder"
              class="expression-editor__status"
            />
            <div v-else />
          </Transition>
          <Transition
            name="validation-fade"
            mode="out-in"
          >
            <div
              v-if="showStatusPopup"
              class="expression-editor__validation-status"
            >
              <span
                class="expression-editor__validation-status--danger"
              >
                {{ $t("advancedsearch.expressions.status.invalid.header") }}
              </span>
              <span>
                {{ $t("advancedsearch.expressions.status.invalid.text") }}
              </span>
            </div>
          </Transition>
        </div>
      </div>
      <div class="expression-editor__controls">
        <div class="expression-editor__infos">
          <p
            id="expressionControlsLabel"
            class="small expression-editor__label"
          >
            {{ $t("advancedsearch.expressions.operators") }}
          </p>
          <Tooltip>
            <p>
              {{ $t("advancedsearch.expressions.tooltip") }}
            </p>
          </Tooltip>
        </div>
        <div
          class="expression-editor__keyboard"
          aria-labelledby="expressionControlsLabel"
        >
          <div class="operators btn-group">
            <button
              v-for="op in ['(', ')']"
              :key="op"
              class="control-btn"
              @click.prevent="addToSequence(op)"
            >
              <span>{{ op }}</span>
            </button>
            <button
              v-for="op in ['and', 'or', 'not']"
              :key="op"
              class="control-btn"
              @click.prevent="addToSequence(op)"
            >
              <span>{{ $t(`logical.${op}.symbol`) }}</span><span>{{ $t(`logical.${op}.label`) }}</span>
            </button>
          </div>
          <div class="remover btn-group">
            <button
              class="control-btn"
              :disabled="currentIndex === 0"
              @click.prevent="removeAtIndex(currentIndex-1)"
            >
              <svg
                width="24"
                height="18"
                xmlns="http://www.w3.org/2000/svg"
              ><path
                d="M22 0H7.1c-.7 0-1.3.3-1.7.9L0 9l5.4 8.1c.4.5 1 .9 1.7.9H22c1.1 0 2-.9 2-2V2c0-1.1-.9-2-2-2zm-3 12.6L17.6 14 14 10.4 10.4 14 9 12.6 12.6 9 9 5.4 10.4 4 14 7.6 17.6 4 19 5.4 15.4 9l3.6 3.6z"
                fill="var(--slate)"
                fill-rule="evenodd"
                opacity=".6"
              /></svg>
            </button>
          </div>
        </div>
        <section class="option-list">
          <ul>
            <li
              v-for="idx in availableIndices"
              :key="idx"
              class="item"
              @click="addToSequence(idx)"
            >
              <FieldInfo
                :field="modelValue.fields[idx]"
              />
            </li>
          </ul>
        </section>
      </div>
    </div>
  </main>
</template>

<script>

import FieldInfo from '#src/components/advanced-search/expression-editor/FieldInfo.vue'
import ParenthesisNode from '#src/components/advanced-search/expression-editor/ParenthesisNode.vue'
import OperationNode from '#src/components/advanced-search/expression-editor/OperationNode.vue'
import Tooltip from '#src/components/utils/Tooltip.vue'
import RecordSearchFieldsHelpers from '#src/mixins/RecordSearchFieldsHelpers'
import SearchExpressionHelpers from '#src/mixins/SearchExpressionHelpers'

export default {
  components: {
    FieldInfo,
    ParenthesisNode,
    OperationNode,
    Tooltip
  },
  mixins: [RecordSearchFieldsHelpers, SearchExpressionHelpers],
  props: {
    modelValue: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      currentIndex: 0,
      borderInterval: null,
      showStatusBorder: false,
      popupInterval: null,
      showStatusPopup: false,
      shownPopup: false
    }
  },
  computed: {
    availableIndices: function () {
      return this.getAvailableFieldIndices(this.modelValue.fields)
    }
  },
  watch: {
    'value.sequence': function (newSequence) {
      this.modelValue.query = this.getQueryFromSequence(newSequence)

      let indentation = 0
      newSequence.forEach((element) => {
        if (element.value === '(') {
          element.indentationLevel = Math.max(0, indentation)
          indentation++
        } else if (element.value === ')') {
          indentation--
          element.indentationLevel = Math.max(0, indentation)
        } else {
          element.indentationLevel = Math.max(0, indentation)
        }
      })
      this.validateExpression()
      this.scrollDisplayToTrack()
    }
  },
  async created() {
    const filled = this.getAvailableFieldIndices(this.modelValue.fields)
    this.modelValue.sequence = this.sequenceFromIndices(this.modelValue.sequence, filled)
    this.currentIndex = this.modelValue.sequence.length

    await this.$nextTick()
    this.validateExpression(0)
  },
  methods: {
    handleInput(e) {
      if (e.key === 'Enter' || e.key === 'Escape' || e.key === 'Tab') {
        return
      }
      e.preventDefault()
      if (e.key === 'j' || e.key === 'ArrowDown') {
        this.setCursor(this.currentIndex + 1)
      } else if (e.key === 'k' || e.key === 'ArrowUp') {
        this.setCursor(this.currentIndex - 1)
      } else if (e.key === 'e' || e.key === 'a') {
        this.addToSequence('and')
      } else if (e.key === 'o') {
        this.addToSequence('or')
      } else if (e.key === 'n') {
        this.addToSequence('not')
      } else if (e.key === '(') {
        this.addToSequence('(')
      } else if (e.key === ')') {
        this.addToSequence(')')
      } else if (e.key === 'Backspace') {
        this.removeAtIndex(this.currentIndex - 1)
      } else if (e.key === 'Delete') {
        this.removeAtIndex(this.currentIndex)
      }
    },
    scrollDisplayToTrack() {
      const ref = this.$refs[`track-${this.currentIndex}`]
      // $refs returns array if inside a v-for, we have both inside a v-for and outside (for index 0)
      const element = Array.isArray(ref) ? ref[0] : ref
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'end' })
      }
    },
    async fillSequence(operator) {
      this.modelValue.sequence = this.getDefaultSequence(this.modelValue.fields, operator)
      this.currentIndex = this.modelValue.sequence.length
      await this.$nextTick()
      this.$refs.expressionWrapper?.focus()
    },
    async clearSequence() {
      this.modelValue.sequence = []
      this.currentIndex = 0

      await this.$nextTick()
      this.$refs.expressionWrapper?.focus()
    },
    async addToSequence(value, moveCursor = true) {
      this.modelValue.sequence.splice(this.currentIndex, 0, this.createNode(value))
      if (moveCursor) {
        await this.$nextTick()
        this.setCursor(this.currentIndex + 1)
      }
      if (value === '(') {
        this.addToSequence(')', false)
      }
    },
    async removeAtIndex(idx) {
      if (idx < 0) {
        return
      }

      const sequence = this.modelValue.sequence
      if (sequence[idx].value === '('
        && sequence[idx + 1]
        && sequence[idx + 1].value === ')') {
        sequence.splice(idx + 1, 1)
      }
      sequence.splice(idx, 1)
      if (this.currentIndex > idx) {
        this.setCursor(this.currentIndex - 1)
      }
    },
    setCursor(idx, scrollDisplay = true) {
      const lastIndex = this.currentIndex
      this.currentIndex = Math.min(Math.max(0, idx), this.modelValue.sequence.length)
      if (scrollDisplay && this.currentIndex !== lastIndex) {
        this.scrollDisplayToTrack()
      }
    },
    validateExpression(delay = 2500, warningDuration = 5000) {
      this.modelValue.status = this.expressionStatus(this.modelValue.query)
      this.animateBorder(delay, warningDuration)
    },
    animateBorder(delay, warningDuration) {
      clearTimeout(this.borderInterval)
      this.borderInterval = null

      if (this.modelValue.status === 'valid' || this.modelValue.status === 'default') {
        // clear all warnings
        this.showStatusBorder = this.showStatusPopup = this.showStatusPopup = this.shownPopup = false
        this.popupInterval = null
        clearTimeout(this.popupInterval)
      } else {
        this.borderInterval = setTimeout(() => {
          this.showStatusBorder = true
          if (!this.shownPopup) {
            this.showStatusPopup = this.shownPopup = true
            this.popupInterval = setTimeout(() => {
              this.showStatusPopup = false
            }, warningDuration)
          }
        }, delay)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@use "sass:color";
@use "#src/assets/scss/variables" as *;

$display-left-padding: 1rem;
$display-right-padding: 4rem;
$display-height: 30rem;

.expression-editor-modal {
  .expression-editor__grid {
    display: grid;
    grid-template: 100% / 1fr 19rem;
    gap: 1rem;
  }

  .expression-editor__infos {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin: 0.25rem 0 1rem;
  }

  .expression-editor__display-wrapper {
    position: relative;
    box-shadow: 0 2px 4px 0 rgb(0  0 0 / 10%);
    transition: box-shadow 0.3s ease-in-out;

    &--danger {
      outline: 3px solid rgb(204 40 64 / 40%);
      box-shadow: 0 0 2px red;
    }

    .control-btn {
      position: absolute;
      width: 3rem;
      height: 3rem;
      z-index: 2;
      box-shadow: 0 2px 4px 0 rgb(0 0 0 / 10%);
    }
  }

  .expression-editor__display {
    // FIXME: this height shouldn't be fixed, we need to talk about the container for this modal
    overflow-y: scroll;
    position: relative;
    height: $display-height;
    padding: 1rem $display-right-padding 1rem $display-left-padding;
    background: $light-blue;
  }

  .expression-editor__validation-status {
    position: absolute;
    float: left;
    border-radius: 2px;
    top: 0;
    left: 0;
    width: 100%;
    padding: 1rem;
    z-index: 2;
    background: white;
    box-shadow: 0 4px 3px -2px rgb(0 0 0 / 10%);

    span {
      text-transform: uppercase;
      font-size: 0.6875rem;
      font-weight: 600;
      letter-spacing: 0.0063rem;
    }

    &--success {
      color: $green;
    }

    &--danger {
      color: $magenta;
    }
  }
}

.expression-editor__placeholder {
  position: absolute;
  width: 60%;
  text-align: center;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1;

  .expression-editor__placeholder-controls {
    margin: 1.5rem 0;
    display: flex;
    justify-content: center;
    gap: 0.5rem;
  }
}

.expression-editor__placeholder-button {
  border: 1px solid $gray-300;
  padding: 1rem;
  width: 35%;
  transition-property: border, background, color, box-shadow;
  transition-duration: 0.2s;
  transition-timing-function: ease-in-out;
  background: white;

  &:focus,
  &:hover {
    box-shadow: 0 2px 4px 0 rgb(0 0 0 / 10%);
  }

  .empty {
    background: $gray-300;
    height: 1rem;
  }

  p {
    margin-top: 0.25rem;
    font-size: 0.75rem;
    font-weight: 600;
    line-height: 14px;
    color: $gray;
  }

  .description {
    max-width: 5.5rem;
    text-align: center;
    margin: 2.25rem auto 0;
  }
}

.expression-editor__placeholder-separator {
  margin-bottom: 1rem;

  &::before,
  &::after {
    content: "";
    display: inline-block;
    vertical-align: middle;
    background: $gray-300;
    height: 1px;
    width: 7.375rem;
  }

  &::before {
    margin-right: 1rem;
  }

  &::after {
    margin-left: 1rem;
  }
}

.track {
  margin-left: -$display-left-padding;
  margin-right: -$display-right-padding;
  width: auto;
  position: relative;
  opacity: 0;
  height: 1.5rem;
  background: repeating-linear-gradient(to right, #5856d6 0, #5856d6 0.5rem, transparent 0.5rem, transparent 0.8rem) center;
  background-size: 100% 0.125rem;
  background-repeat: no-repeat;

  &::before {
    content: "";
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 0;
    height: 0;
    border-top: 0.5rem solid transparent;
    border-bottom: 0.5rem solid transparent;
    border-left: 0.5rem solid $violet;
  }

  &::after {
    content: "";
    position: absolute;
    right: 0;
    top: 50%;
    transform: translateY(-50%);
    height: 0.5rem;
    border-left: 2px solid $violet;
    padding-top: 15px;
  }
}

.track:hover,
.item:hover + .track:not(.current) {
  opacity: 0.3;
}
.track.current {
  // animation: blinkCursor 1.5s step-end 3;
  opacity: 1;
}

.expression-editor__label {
  font-weight: 600;
}

$keyboard-height: 3rem;
$keyboard-margin: 1rem;

.option-list {
  height: calc(#{$display-height} - #{$keyboard-height} - #{$keyboard-margin});
  overflow-y: scroll;

  ul > li:not(:last-child) {
    margin-bottom: 1rem;
  }
}

.expression-editor__keyboard {
  height: $keyboard-height;
  margin-bottom: $keyboard-margin;
  display: flex;
  align-items: center;
  justify-content: space-between;

  & * {
    font-size: 0.625rem;
    font-weight: 600;
    letter-spacing: 0.0063rem;
  }

  span + span {
    margin-top: 0.3125rem;
  }

  .operators,
  .remover {
    height: 100%;
    box-shadow: 0 2px 4px 0 rgb(0 0 0 / 10%);
  }
}

.control-btn {
  width: 3rem;
  height: 100%;
  background: white;
  text-transform: uppercase;
  display: grid;
  place-content: center;
  justify-items: center;
  transition-property: border, background, color;
  transition-duration: 0.2s;
  transition-timing-function: ease-in-out;
  color: $slate;
  &:hover,
  &:focus {
    background-color: $light-blue-600;
  }

  &[disabled] {
    opacity: 0.3;
  }
}

// @keyframes blinkCursor {
//   from { opacity: 1; }
//   25% { opacity: 0; }
//   50% { opacity: 1; }
// }
</style>
